import { Badge, Button, Card, Checkbox, Row, Space } from 'antd';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import CloseableErrorMessage from '../../components/CloseableErrorMessage';
import { Company } from '../../models/company-interface';
import { CompanyUserProfile } from '../../models/company-user-profile-interface';
import { CSSearchResults } from '../../models/cs-search-results-interface';
import { CustomerServiceQueries } from '../../queries/customer-service-queries';
import { cancel } from '../../utilites/api';
import { HasRight, Rights } from '../../utilites/auth';
import { TableAndExcelColumn } from '../../utilites/exportToExcel';
import SearchExport from './SearchExport';
import CompanyResultsTab from './tabs/CompanyResultsTab';
import UserResultsTab from './tabs/UserResultsTab';
import CreateDeletionRequestsModal from './modals/CreateDeletionRequestsConfirm';

function SearchPage() {
  const history = useHistory();
  const [searchText, setSearchText] = useState<string | null>(null);
  const searchQuery = CustomerServiceQueries.useSearch(searchText);
  const [activeKey, setActiveKey] = useState<string>('users');
  const location = useLocation();
  const [excelColumns, setExcelColumns] = useState<TableAndExcelColumn<CompanyUserProfile>[] | TableAndExcelColumn<Company>[]>([]);
  const [filteredData, setFilteredData] = useState<CSSearchResults | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [showDeletionModal, setShowDeletionModal] = useState<boolean>(false);

  const deleteRequestMutator = CustomerServiceQueries.useDeleteCompany();

  const isLoading = searchQuery.isLoading && searchQuery.isFetching;

  const query = new URLSearchParams(window.location.search);
  const searchType = window.location.href.includes("/users") ? "users" : 
                      window.location.href.includes("/companies") ? "companies" : 
                      window.location.href.includes("/demo") ? "demo" : 
                      window.location.href.includes("/trial") ? "trial" : 
                      window.location.href.includes("/disabled") ? "disabled" : 
                      window.location.href.includes("/delete") ? "delete" : 
                      window.location.href.includes("/access") ? "access" : 
                      window.location.href.includes("/parentchild") ? "parentchild" : 
                      window.location.href.includes("/subscription") ? "subscription" : 
                      window.location.href.includes("/feature") ? "feature" : 
                      "all";

  const companyOnly = ["companies","demo","trial","delete","disabled","subscription","parentchild","feature"]
  const userOnly = ["users","access"]
  const notDemo = searchType === "demo" || searchType == "trial"
  
  const [excludeDemo,setExcludeDemo] = useState<boolean>(true);
  const [demoFiltered, setDemoFiltered] = useState<CSSearchResults>(null);

  useEffect(() => {
    document.title = `Govenda CS: Search`;

    return () => {
      cancel('Search page unmounted');
    };
  }, []);

  useEffect(() => {
    if (searchType === 'all' && !!searchQuery && !!searchQuery.data) {
      if (
        searchQuery.data.users.length === 0 &&
        activeKey === 'users' &&
        searchQuery.data.companies.length > 0
      ) {
        setActiveKey('companies');
      } else if (
        searchQuery.data.companies.length === 0 &&
        activeKey === 'companies' &&
        searchQuery.data.users.length > 0
      ) {
        setActiveKey('users');
      }
    }

    if(!!searchQuery.data) {
        setDemoFiltered({...searchQuery.data,users: searchQuery.data.users.filter(x => (excludeDemo ? !x.isDemoCompany : true)), companies: searchQuery.data.companies.filter(x => (excludeDemo ? !x.isDemoCompany : true))})
    }
    setFilteredData(searchQuery.data ?? null);
  }, [searchQuery.data]);

  useEffect(() => {
    if(!!searchQuery.data) {
        setDemoFiltered({...searchQuery.data,users: searchQuery.data.users.filter(x => (excludeDemo ? !x.isDemoCompany : true)), companies: searchQuery.data.companies.filter(x => (excludeDemo ? !x.isDemoCompany : true))})
    }
    },[excludeDemo])

  useEffect(() => {
    if (customSearchTerm() !== null) {
      console.log('custom search page', customSearchTerm());
      search(customSearchTerm());
    } else {
      search(query.get('query'));
    }

    return () => {
      cancel('Search page unmounted');
    };
  }, [location]);

  const isCustom = (): boolean => {
    return companyOnly.includes(searchType) || userOnly.includes(searchType);
  };

  const customSearchTerm = (): string | null => {
    if (searchType === 'demo') return 'isdemo';
    if (searchType === 'trial') return 'intrial';
    if (searchType === 'disabled') return 'disabled';
    if (searchType === 'delete') return 'readytodelete';
    if (searchType === 'access') return 'hasaccess';
    if (searchType === 'parentchild') return 'parentchild';
    return null;
  };

  const customTitle = (): string | null => {
    if (searchType === 'demo') return 'Demo Companies';
    if (searchType === 'trial') return 'In-Trial Companies';
    if (searchType === 'disabled') return 'Disabled Companies';
    if (searchType === 'parentchild') return 'Parent/Child Companies (Legacy)';
    if (searchType === 'delete') return 'Companies Ready To Delete';
    if (searchType === 'access') return 'Users Who Have Granted Access';
    if (searchType === 'subscription') return `Companies with subscription matching: ${query.get('query')}`;
    if (searchType === 'feature') return `Companies with features matching: ${query.get('query')}`;
    return null;
  };

  const search = (searchString: string | null) => {
    if (!searchString?.length)
      CloseableErrorMessage('Please supply search text.');
    else {
      let term =
        searchType === 'all' ? searchString : 
        searchType === 'feature' ? `feature=${searchString}` : 
        searchType === 'subscription' ? `subscription=${searchString}` : 
        searchType === 'users' ? `user=${searchString}` : 
        searchType === 'companies' ? `company=${searchString}` : 
        searchString;
      setSearchText(encodeURIComponent(term));
    }
  };

  const tabList =
    !!searchQuery.data && !isCustom()
      ? [
          {
            key: 'users',
            tab: (
              <>
                Users <Badge count={searchQuery.data.users.length} />
              </>
            ),
            disabled: searchQuery.data.users.length === 0,
          },
          {
            key: 'companies',
            tab: (
              <>
                Companies <Badge count={searchQuery.data.companies.length} />
              </>
            ),
            disabled: searchQuery.data.companies.length === 0,
          },
        ]
      : [];

  const filterData = (
    dataType: string,
    newFilteredData: Company[] | CompanyUserProfile[]
  ) => {
    if (dataType === 'users') {
      setFilteredData({
        ...searchQuery.data!,
        users: newFilteredData as CompanyUserProfile[],
      });
    } else {
      setFilteredData({
        ...searchQuery.data!,
        companies: newFilteredData as Company[],
      });
    }
  };

  const contentList: Record<string, React.ReactNode> = !!searchQuery.data
    ? {
        users: (
          <UserResultsTab
            users={demoFiltered?.users ?? []}
            access={searchType === 'access'}
            excludeDemo={excludeDemo || notDemo}
            setExcelColumns={setExcelColumns}
            setFilteredData={filterData}
          />
        ),
        companies: (
          <CompanyResultsTab
            companies={demoFiltered?.companies ?? []}
            parentChild={searchType === 'parentchild'}
            feature={searchType === 'feature'}
            excludeDemo={excludeDemo || notDemo}
            setExcelColumns={setExcelColumns}
            setFilteredData={filterData}
            allowDeletion={searchType === 'delete'}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        ),
      }
    : {};
  
  const deletionEnabled = selectedRowKeys.length > 0;
  const handleDeletionRequestSubmit = (values: any) => {
    deleteRequestMutator.mutateAsync(selectedRowKeys).then(() => {
      setShowDeletionModal(false);
      setSelectedRowKeys([]);
    });
  };

  const hasData = !!filteredData?.users?.length || !!filteredData?.companies?.length || 
                  !!searchQuery.data?.users?.length || !!searchQuery.data?.companies?.length

  return (
    <Card
      loading={isLoading}
      title={
        customTitle() != null
          ? customTitle()
          : `${
              searchType === 'all'
                ? 'Search'
                : searchType === 'users'
                ? 'User'
                : 'Company'
            } results for: ${query.get('query')}`
      }
      tabList={tabList}
      activeTabKey={activeKey}
      onTabChange={(key) => {
        setActiveKey(key);
      }}
      extra={<Space>{searchType === "demo" && HasRight([Rights.CSAdmin]) && <Button href="/admin/products/demo" onClick={(e) => {
          e.preventDefault()
          history.push("/admin/products/demo")
      }}>Manage Demo Features</Button>}
        <SearchExport
          getColumns={() => excelColumns}
          getData={() => filteredData ?? searchQuery.data}
          getActiveKey={() => activeKey}
          excludeDemo={excludeDemo}
          allowDeletion={searchType === 'delete'}
          fileName={
            customTitle() != null
              ? customTitle()!
              : `${
                searchType === 'all' && activeKey === "users"
                  ? 'User'
                  : searchType === 'all' && activeKey === "companies" 
                  ? "Company"
                  : searchType === 'users'
                  ? 'User'
                  : 'Company'
              } results for ${query.get('query')}`
          }
          disabled={isLoading || !hasData}
          deletionEnabled={deletionEnabled}
          setShowDeletionModal={setShowDeletionModal}
        /></Space>
      }
    >
    {!notDemo && <Row justify="end" style={{marginBottom: 20}}>
        <Checkbox checked={excludeDemo} onChange={(v) => setExcludeDemo(v.target.checked)}>Exclude Demo Companies From Results</Checkbox>
    </Row>}
      {!!searchQuery.data && searchType === 'all'
        ? contentList[activeKey]
        : contentList[companyOnly.includes(searchType) ? 'companies' : 'users']}
    <CreateDeletionRequestsModal
      isVisible={showDeletionModal}
      setIsVisible={setShowDeletionModal}
      handleSubmit={handleDeletionRequestSubmit}
      isLoading={deleteRequestMutator.isLoading}
      selectedRowKeys={searchQuery.data?.companies.filter(x => selectedRowKeys.includes(x.id)) ?? []}
    />
    </Card>
  );
}

export default SearchPage;
